"use client";

import { useState } from "react";
import { Product } from "@/actions/products";
import { ProductCard } from "./ProductCard";
import { QuickViewModal } from "./QuickViewModal";
import { SlidersHorizontal, ArrowDownWideNarrow, ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";

interface ShopListingProps {
    products: Product[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
}

export function ShopListing({ products, totalCount, totalPages, currentPage }: ShopListingProps) {
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const router = useRouter();
    const searchParams = useSearchParams();

    const handleQuickView = (product: Product) => {
        setSelectedProduct(product);
        setIsQuickViewOpen(true);
    };

    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set("sort", e.target.value);
        params.set("page", "1"); // Reset to page 1 on sort
        router.push(`/products?${params.toString()}`, { scroll: false });
    };

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        const params = new URLSearchParams(searchParams.toString());
        params.set("page", page.toString());
        router.push(`/products?${params.toString()}`, { scroll: true });
    };

    return (
        <div className="flex-grow">
            {/* Toolbar */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 mb-6 flex flex-wrap items-center justify-between gap-4 shadow-sm">
                <p className="text-gray-600 text-sm">
                    Showing <span className="font-bold text-gray-900">{products.length}</span> of {totalCount} results
                </p>

                <div className="flex items-center gap-4">
                    {/* Mobile Filter Toggle */}
                    <button className="lg:hidden flex items-center gap-2 text-gray-600 bg-gray-50 px-4 py-2 rounded-lg hover:bg-gray-100 font-medium text-sm">
                        <SlidersHorizontal className="w-4 h-4" /> Filters
                    </button>

                    {/* Sort */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 hidden sm:inline">Sort by:</span>
                        <div className="relative">
                            <select
                                onChange={handleSortChange}
                                defaultValue={searchParams.get("sort") || "popularity"}
                                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:border-blue-500 cursor-pointer"
                            >
                                <option value="popularity">Popularity</option>
                                <option value="newest">Newest Arrivals</option>
                                <option value="price_asc">Price: Low to High</option>
                                <option value="price_desc">Price: High to Low</option>
                            </select>
                            <ArrowDownWideNarrow className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Grid */}
            {products.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                    {products.map((product) => (
                        <ProductCard
                            key={product.id}
                            product={product}
                            onQuickView={() => handleQuickView(product)}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-100">
                    <p className="text-gray-500 text-lg font-medium">No products found matching your filters.</p>
                    <button
                        onClick={() => router.push("/products")}
                        className="mt-4 text-blue-600 hover:underline font-bold"
                    >
                        Clear All Filters
                    </button>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-12 flex justify-center gap-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="w-10 h-10 rounded-lg bg-white text-gray-600 font-bold border border-gray-200 hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        const isCurrent = page === currentPage;
                        return (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`w-10 h-10 rounded-lg font-bold flex items-center justify-center transition-all ${isCurrent
                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                    }`}
                            >
                                {page}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="w-10 h-10 rounded-lg bg-white text-gray-600 font-bold border border-gray-200 hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Quick View Modal */}
            {selectedProduct && (
                <QuickViewModal
                    product={selectedProduct}
                    isOpen={isQuickViewOpen}
                    onClose={() => setIsQuickViewOpen(false)}
                />
            )}
        </div>
    );
}
